<template>
  <form v-if="model" class="mt-2 row g-3 align-items-baseline">
    <div class="form-group col-md-3">
      <label class="dx-field-item-label-text" for="inputEstado">{{ $t('facturas.modal_detalle.estado') }}</label>
      <div class="mt-1">
        <div :class="['d-inline-block rounded py-1 px-2 ', 'estado-factura', model.Estado]">{{ $t(`facturas.estado.${model.Estado}`) }}</div>
      </div>
    </div>
    <div class="form-group col-md-3">
      <label class="dx-field-item-label-text" for="inputIdentificadorBorrador">{{ $t('facturas.modal_detalle.identificador_borrador') }}</label>
      <div class="mt-1" id="inputIdentificadorBorrador">{{ model.IdentificadorBorrador }}</div>
    </div>
    <div class="form-group col-md-3">
      <label class="dx-field-item-label-text" for="inputIdentificador">{{ $t('facturas.modal_detalle.identificador') }}</label>
      <div class="mt-1" id="inputIdentificador">{{ model.Identificador }}</div>
    </div>
    <div class="form-group col-md-3">
      <label class="dx-field-item-label-text" for="inputFechaEmision">{{ $t('facturas.modal_detalle.fecha_emision') }}</label>
      <div class="mt-1" id="inputFechaEmision">{{ fechaEmisionFormateada }}</div>
    </div>
    <div v-if="model.MotivoRechazo" class="form-group col-md-12">
      <label class="dx-field-item-label-text" for="inputMotivoRechazo">{{ $t('facturas.modal_detalle.motivo_rechazo') }}</label>
      <div class="mt-1" id="inputMotivoRechazo">{{ model.MotivoRechazo }}</div>
    </div>
    <div v-if="esUsuarioBO" class="form-group col-md-3">
      <label class="dx-field-item-label-text" for="inputTipoEmpresaAgentes">{{ $t('facturas.modal_detalle.tipo_empresa_agentes') }}</label>
      <div class="mt-1" id="inputTipoEmpresaAgentes">{{ $t(`empresa_agente.tipo.${model.TipoEmpresaAgentes}`) }}</div>
    </div>
    <div v-if="esUsuarioBO" class="form-group col-md-3">
      <label class="dx-field-item-label-text" for="inputEmpresaAgentes">{{ $t('facturas.modal_detalle.empresa_agentes') }}</label>
      <div class="mt-1" id="inputEmpresaAgentes">{{ model.EmpresaAgentes }}</div>
    </div>
    <div class="form-group col-md-12">
      <label class="dx-field-item-label-text" for="inputNombreEmisor">{{ $t('facturas.modal_detalle.nombre_emisor') }}</label>
      <div class="mt-1" id="inputNombreEmisor">{{ model.NombreEmisor }}</div>
    </div>
    <div class="col-md-12">
      <div class="row g-3 align-items-stretch">
        <div class="form-group col-md-3 d-flex flex-column">
          <label class="dx-field-item-label-text">{{ $t('facturas.modal_detalle.calculo_impuestos') }}</label>
          <div class="economic-section mt-1 flex-grow-1">
            <div class="economic-row">
              <span class="economic-label">{{ $t('facturas.modal_detalle.base_imponible') }}</span>
              <span class="economic-value">{{ formatImporte(model.BaseImponible) }}</span>
            </div>
            <div class="economic-row">
              <span class="economic-label">{{ $t('facturas.modal_detalle.impuesto_iva') }}</span>
              <span class="economic-value">{{ formatImporte(model.ImpuestoIva) }}</span>
            </div>
            <div class="economic-row">
              <span class="economic-label">{{ $t('facturas.modal_detalle.retencion_irpf') }}</span>
              <span class="economic-value">-{{ formatImporte(model.RetencionIrpf) }}</span>
            </div>
            <div class="economic-row total py-2">
              <span class="economic-label">{{ $t('facturas.modal_detalle.importe_total') }}</span>
              <span class="economic-value">{{ formatImporte(model.ImporteTotal) }}</span>
            </div>
          </div>
        </div>
        <div class="form-group col-md-9 d-flex flex-column">
          <label class="dx-field-item-label-text" for="inputNotas">{{ $t('facturas.modal_detalle.notas') }}</label>
          <div class="mt-1 flex-grow-1" id="inputNotas">{{ model.Notas }}</div>
        </div>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { FacturaDetailModel } from "@api/api";
import { useIntl } from "@services/i18n";
import { useGrupoUsuarioChecker } from "@services/auth/authorization/grupo-usuario-checker";

const props = defineProps<{
  model: FacturaDetailModel | null;
}>();

const intl = useIntl();
const { esBackoffice } = useGrupoUsuarioChecker();

const esUsuarioBO = computed(() => esBackoffice());

const fechaEmisionFormateada = computed(() => {
  if (!props.model?.FechaEmision) return "";
  return new Date(props.model.FechaEmision).toLocaleDateString();
});

function formatImporte(value: number | null | undefined): string {
  const v = value ?? 0;
  return `${intl.i18n.n(Number(v.toFixed(2)), "decimal")} €`;
}
</script>

<style lang="scss" scoped>
@import "@styles/_sharedvars.scss";


#inputNotas,
#inputMotivoRechazo {
  white-space: pre-line;
  height: auto;
}

.economic-section {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .economic-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;


    .economic-label {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .economic-value {
      text-align: right;
      white-space: nowrap;
    }

    &.total {
      border-top: 1px solid $base-border-color;

      .economic-label,
      .economic-value {
        font-weight: 600;
      }
    }
  }
}

.status-badge {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 5px;
  text-align: center;
}
</style>
